import { createSlice } from '@reduxjs/toolkit';
import { setCookie, hasCookie, deleteCookie } from 'cookies-next';

import {
  ARR_USER_ROLES,
  SESSION_EXPIRY,
  TEMP_USER_TOKEN,
  USER_TOKEN,
  USER_VIEW_MODE,
} from 'helpers/storage';
import userApi from 'api/userApi';
import { USER_ROLES } from 'constants/constants';

const initialState = {
  user: {},
  /**
   * {string} instructor
   * {string} admin
   * {string} learner
   */
  viewMode: USER_ROLES.LEARNER,
  language: 'en',
  languages: [],
  tempUserToken: '',
  shouldDisconnectSocket: undefined,
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    getUser: (state, action) => {
      state.user = action.payload;
      ARR_USER_ROLES.set(action.payload?.roles);
    },
    logout: (state) => {
      SESSION_EXPIRY.delete();
      deleteCookie('USER_TOKEN');
      setCookie('userViewMode', USER_ROLES.LEARNER);
      deleteCookie('userViewMode');
      deleteCookie('tempUserToken');
      return initialState;
    },
    changeViewMode: (state, action) => {
      state.viewMode = action.payload;
      USER_VIEW_MODE.set(action.payload);
    },
    changeLanguage: (state, action) => {
      state.language = action.payload;
    },
    getListLanguages: (state, action) => {
      state.languages = action.payload;
    },
    setTempUserToken: (state, action) => {
      state.tempUserToken = action.payload;
      if (action.payload) {
        setCookie('tempUserToken', action.payload);
        TEMP_USER_TOKEN.set(action.payload);
      } else {
        deleteCookie('tempUserToken');
        TEMP_USER_TOKEN.delete();
      }
    },
    setShouldDisconnectSocket: (state, action) => {
      state.shouldDisconnectSocket = Date.now();
    },
  },
});

// async action
export const checkUserAsync = () => async (dispatch) => {
  const userLoggedIn = await userApi.get();

  if (USER_TOKEN.get()) {
    if (!hasCookie('userToken')) {
      setCookie('userToken', USER_TOKEN.get());
    }
    setCookie('userRoles', userLoggedIn?.roles);
    setCookie(
      'userViewMode',
      userLoggedIn?.type?.name === USER_ROLES.SUPER_ADMIN
        ? USER_ROLES.ADMIN
        : userLoggedIn?.type?.name,
    );
  }
  if (
    userLoggedIn?.roles?.indexOf(USER_ROLES.SUPER_ADMIN) !== -1 ||
    userLoggedIn?.roles?.indexOf(USER_ROLES.ADMIN) !== -1
  ) {
    dispatch(changeViewMode(USER_ROLES.ADMIN));
  } else if (userLoggedIn?.roles?.indexOf(USER_ROLES.INSTRUCTOR) !== -1) {
    dispatch(changeViewMode(USER_ROLES.INSTRUCTOR));
  } else if (userLoggedIn?.roles?.indexOf(USER_ROLES.CENTRE_ADMIN) !== -1) {
    dispatch(changeViewMode(USER_ROLES.CENTRE_ADMIN));
  } else if (userLoggedIn?.roles?.indexOf(USER_ROLES.CENTRE_BL) !== -1) {
    dispatch(changeViewMode(USER_ROLES.CENTRE_BL));
  } else {
    dispatch(changeViewMode(USER_ROLES.LEARNER));
  }
  return dispatch(getUser(userLoggedIn));
};

export const getListLanguageAsync = () => async (dispatch) => {
  const languages = await userApi.getLanguage();
  return dispatch(getListLanguages(languages));
};

export const loginUserAsync = (user, config) => async (dispatch) => {
  const userLoggedIn = await userApi.login(user, config);
  if (
    userLoggedIn?.roles?.indexOf(USER_ROLES.SUPER_ADMIN) !== -1 ||
    userLoggedIn?.roles?.indexOf(USER_ROLES.ADMIN) !== -1
  ) {
    dispatch(changeViewMode(USER_ROLES.ADMIN));
  } else if (userLoggedIn?.roles?.indexOf(USER_ROLES.INSTRUCTOR) !== -1) {
    dispatch(changeViewMode(USER_ROLES.INSTRUCTOR));
  } else if (userLoggedIn?.roles?.indexOf(USER_ROLES.CENTRE_ADMIN) !== -1) {
    dispatch(changeViewMode(USER_ROLES.CENTRE_ADMIN));
  } else if (userLoggedIn?.roles?.indexOf(USER_ROLES.CENTRE_BL) !== -1) {
    dispatch(changeViewMode(USER_ROLES.CENTRE_BL));
  } else {
    dispatch(changeViewMode(USER_ROLES.LEARNER));
  }
  setCookie('userToken', userLoggedIn.token);
  USER_TOKEN.set(userLoggedIn.token);
  return dispatch(getUser(userLoggedIn));
};
/**
 *
 * @param {*} state
 * @returns {import('api/userApi').UserProps}
 */
export const getUserLogin = (state) => state.auth.user;

/**
 * @param {*} state
 * @returns {keyof USER_ROLES}
 */
export const getViewMode = (state) => state.auth.viewMode;
export const getLanguage = (state) => state.auth.language;
export const listLanguages = (state) => state.auth.languages;
export const getTempUserToken = (state) => state.auth.tempUserToken;
export const getShouldDisconnectSocket = (state) =>
  state.auth.shouldDisconnectSocket;
// Actions
export const {
  getUser,
  logout,
  changeViewMode,
  changeLanguage,
  getListLanguages,
  setTempUserToken,
  setShouldDisconnectSocket,
} = authSlice.actions;
export default authSlice.reducer;
